import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { ensureUrl } from "@/lib/format-url"
import type { BreadcrumbItem } from "@/types"

interface CompanyData {
  id: number
  name: string
  nickname: string
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  email: string | null
  website: string | null
  color1: string | null
  color2: string | null
  manager_id: number | null
  manager_name: string | null
  status: number
}

interface Props {
  company: CompanyData
}

export default function Show({ company }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Manage", href: "/admin/companies" },
    { title: "Companies", href: "/admin/companies" },
    { title: company.name, href: `/admin/companies/${company.id}` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={company.name} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{company.name}</h1>
          <Link
            href={`/admin/companies/${company.id}/edit`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Edit
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div>
            {company.color1 && company.color2 && (
              <div className="mb-4 flex gap-2">
                <span
                  className="inline-block h-6 w-6 rounded-full border"
                  style={{
                    backgroundColor: company.color1,
                    borderColor: company.color2,
                  }}
                />
                <span className="text-hpi-med-gray text-sm">
                  {company.color1} / {company.color2}
                </span>
              </div>
            )}
            {company.nickname && (
              <p className="text-hpi-med-gray mb-2">({company.nickname})</p>
            )}
            {company.address && <p>{company.address}</p>}
            {company.address2 && <p>{company.address2}</p>}
            {company.city && (
              <p>
                {company.city}, {company.state} {company.zip}
              </p>
            )}
          </div>
          <div className="text-right">
            {company.manager_name && (
              <p className="mb-2">
                <strong>Manager:</strong> {company.manager_name}
              </p>
            )}
            {company.phone && <p>Phone: {company.phone}</p>}
            {company.email && <p>Email: {company.email}</p>}
            {company.website && (
              <p>
                Website:{" "}
                <a
                  href={ensureUrl(company.website)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-hpi-active-blue hover:underline"
                >
                  {company.website}
                </a>
              </p>
            )}
            <p className="mt-2">
              Status: {company.status === 0 ? "Active" : "Inactive"}
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link
            href="/admin/companies"
            className="text-hpi-active-blue hover:underline"
          >
            Back to Company List
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
